import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './app/store';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './index.css';


// Rendertron waits for window.prerenderReady === true instead of guessing from
// network activity - network-idle detection resolves too early on pages whose
// content depends on slow third-party APIs (e.g. the careers job list, which
// has been observed taking 13s+), so those sections were missing from crawler
// snapshots. This tracks in-flight fetch() calls generically across the app
// and flips the flag once everything settles, with a hard ceiling so one
// slow/broken endpoint can't block a render forever.
(function setupPrerenderReadySignal() {
  // Only relevant to headless Chrome under Puppeteer/Rendertron - skip the
  // fetch wrapping (extra response clone + body read per request) entirely
  // for real visitors, who have no use for window.prerenderReady.
  if (!navigator.webdriver) {
    return;
  }

  window.prerenderReady = false;
  const MAX_WAIT_MS = 20000;
  const SETTLE_DELAY_MS = 300;
  const INITIAL_GRACE_MS = 500;

  let pending = 0;
  let settleTimer = null;
  let markedReady = false;

  function markReady() {
    if (markedReady) return;
    markedReady = true;
    window.prerenderReady = true;
  }

  const maxWaitTimer = setTimeout(markReady, MAX_WAIT_MS);

  function scheduleSettleCheck() {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      if (pending === 0) {
        clearTimeout(maxWaitTimer);
        markReady();
      }
    }, SETTLE_DELAY_MS);
  }

  // Covers pages that never call fetch() at all.
  setTimeout(scheduleSettleCheck, INITIAL_GRACE_MS);

  function settleOne() {
    pending = Math.max(0, pending - 1);
    scheduleSettleCheck();
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = (...args) => {
    pending += 1;
    clearTimeout(settleTimer);
    return originalFetch(...args).then(
      (response) => {
        // fetch() resolves once headers arrive, well before a large JSON body
        // (e.g. the 68-entry job list) finishes downloading and parsing.
        // Read a clone in parallel so "pending" reflects the full response,
        // without interfering with the app's own response.json()/.text() call.
        response.clone().text().then(settleOne, settleOne);
        return response;
      },
      (err) => {
        settleOne();
        throw err;
      }
    );
  };
})();

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <React.StrictMode>
    <Provider store={store}>

      <App />
      
    </Provider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals(console.log);
